import { Component, computed, inject, input, OnDestroy, OnInit, output, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTabsModule } from '@angular/material/tabs';
import { TranslatePipe } from '@ngx-translate/core';
import { captureException } from '@sentry/angular';
import { StoreHistoryService } from 'src/app/services/store-history/store-history.service';
import {
  SheetSource,
  StoreHistoryTelemetryService,
} from 'src/app/services/store-history/store-history-telemetry.service';
import { HistoryChangesTabComponent } from './changes-tab/changes-tab.component';
import { HistorySnapshotsTabComponent } from './snapshots-tab/snapshots-tab.component';
import { ChangesState } from './history-sheet.types';

/**
 * The History side sheet's contents: a header, two tabs, and whichever state the Changes tab is in.
 *
 * The panel is rendered inside the `mat-sidenav` that `HistorySheetContainerComponent` holds; this
 * component knows nothing about the drawer beyond asking to be closed — the drawer owns the
 * backdrop, Escape, the focus trap and the focus restore. That split is what lets the whole sheet be
 * mounted directly in a spec with no drawer around it.
 */
@Component({
  templateUrl: './history-sheet.component.html',
  standalone: true,
  selector: 'app-history-sheet',
  imports: [
    MatTabsModule,
    MatButtonModule,
    MatIconModule,
    TranslatePipe,
    HistoryChangesTabComponent,
    HistorySnapshotsTabComponent,
  ],
})
export class HistorySheetComponent implements OnInit, OnDestroy {
  private readonly storeHistory = inject(StoreHistoryService);
  private readonly telemetry = inject(StoreHistoryTelemetryService);

  /** Where the sheet was opened from. One run is one opening. */
  readonly source = input<SheetSource>('header');

  private readonly openedAt = Date.now();
  private interacted = false;

  readonly closed = output<void>();

  readonly selectedTab = signal(0);
  private readonly loading = signal(true);
  private readonly failed = signal(false);

  /**
   * Exactly one state, resolved here rather than in the template.
   *
   * The order matters: a store with no snapshot yet is not "empty", it is a store whose history has
   * not started, and telling it there are "no changes" would imply its edits were recorded and
   * reversible when they were not.
   */
  readonly changesState = computed<ChangesState>(() => {
    if (this.loading()) return 'loading';
    if (this.failed()) return 'error';
    if (!this.storeHistory.snapshotHeader()?.id) return 'no-snapshot';
    if (!this.storeHistory.saved().length && !this.storeHistory.draft().length) return 'empty';

    return 'ready';
  });

  ngOnInit(): void {
    this.telemetry.startRun();
    void this.load();
  }

  ngOnDestroy(): void {
    // Dwell and whether anything was touched, together: "opened and did nothing" is a
    // discoverability problem, and "opened, read, closed" is not the same thing as "never opened".
    this.telemetry.sheetAbandoned(Date.now() - this.openedAt, this.interacted);
    this.telemetry.endRun();
  }

  /** Any action taken inside the sheet, so `abandoned` can tell reading from doing. */
  markInteracted(): void {
    this.interacted = true;
  }

  async load(): Promise<void> {
    this.loading.set(true);
    this.failed.set(false);

    try {
      await this.storeHistory.load();
    } catch (error) {
      // A history the merchant cannot load is a panel with a retry button, never a broken header.
      captureException(error);
      this.failed.set(true);
    } finally {
      this.loading.set(false);
    }

    this.telemetry.sheetViewed({
      entryCount: this.storeHistory.saved().length,
      draftCount: this.storeHistory.draft().length,
      hasSnapshot: Boolean(this.storeHistory.snapshotHeader()?.id),
      source: this.source(),
    });
  }
}
